import React, { useRef, useState } from 'react'
import { Button, Divider, Form, Input, message, Tabs, Checkbox, Modal } from 'antd'
import classNames from 'classnames'
import Cookies from 'js-cookie'
import trim from 'lodash/trim'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import { useTranslation, Trans } from '@/libs/next-lang'
import Captcha from '@/components/Captcha'
import BaseLayout from '@/components/LoginModal/components/BaseLayout'
import { COOKIES_MAP, LOGIN_TAB_TYPE, MICRO_APP_MAP } from '@/constants'
import globalStore from '@/globalStore'
import useFbe from '@/hooks/useFbe'
import CountDown from '@/page-components/components/CountDown'
import { sendVerifyCode } from '@/services/common'
import { goSSO, openUrl } from '@/utils'
import gioTrackEvent from '@/utils/gio'
import eventMap from '@/utils/gio/eventMap'
import { setStorage } from '@/utils/storage'
import { emailReg, loginMobileReg, verifyCodeReg } from '@/utils/validate'
import styles from './index.module.less'

const Login = () => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const router = useRouter()
  const [mobileForm] = Form.useForm()
  const [accountForm] = Form.useForm()
  const { language, isFbe } = useFbe()
  const globalMobx = useLocalObservable(() => globalStore)
  const captchaInsRef = useRef(null)

  const [modal, contextHolder] = Modal.useModal()

  const { t } = useTranslation(['user'])

  const checkValidateFetch = () => {
    return globalMobx.checkValidateAjax()
  }

  const changeLoginTab = (val: string, isNewDevice: boolean = false) => {
    globalStore.updateGlobalStore('isNewDevice', isNewDevice && val === LOGIN_TAB_TYPE.CODE)
    globalMobx.updateGlobalStore('lastLoginTab', val)
    setStorage('lastLoginTab', val)
  }

  /**
   * 登录后回调
   * @param res
   * @param targetPathUrl
   * @param type
   * @returns
   */
  const handleCallback = (res: any, targetPathUrl?: any, type?: any) => {
    if (!res) {
      // 网络异常
      setConfirmLoading(false)
      return
    }
    if (res?.code === 0) {
      const result: any = res?.result || {}
      const isBindTag = result.isBindTag
      // 添加埋点信息 手机验证码注册用户
      if (type === 'mobile' && result.isRegister) {
        const channel = Cookies.get(COOKIES_MAP.GIVEAWAY_CHANNEL, {
          domain: '.creativebooster.com',
        })
        if (channel) {
          // 是从活动页面过来的
          gioTrackEvent(eventMap.CB_PC_from_edm_register_click_success, {
            cb_user_register_channel: channel,
          })
        } else {
          gioTrackEvent(eventMap.CB_PC_register_click_success)
        }
      }
      if (isFbe) {
        // fbe页面，仅支持新开页打开编辑器
        if (
          targetPathUrl?.includes(MICRO_APP_MAP.VE) ||
          targetPathUrl?.includes(MICRO_APP_MAP.CANVA_CREACTE) ||
          targetPathUrl?.includes(MICRO_APP_MAP.FBE_CANVA_CREATE)
        ) {
          // ve、可画编辑器新开页
          openUrl(targetPathUrl)
        }
        return
      }
      if (!isBindTag) {
        // 引导页提交状态-未提交
        router.replace('/welcome')
        return
      }
      // 重新登录，自动切换到视频列表
      // if (res?.lastCorporationId) {
      //   // todo: 此处待优化
      //   if (!config.unRedirectPageWhenLogin?.includes?.(pathname)) {
      //     const newUrl = getGlobalRedirectPath()
      //     globalMobx.targetPathUrl = newUrl
      //     // 登录后跳转视频模板列表页面,修复登录前手动切换到首页，此时targetPathUrl 就是‘/’，再登录还是在首页提示了模版上新消息
      //     setTimeout(() => {
      //       router.push(newUrl)
      //     })
      //   }
      // }
      if (
        targetPathUrl?.includes(MICRO_APP_MAP.VE) ||
        targetPathUrl?.includes(MICRO_APP_MAP.CANVA_CREACTE)
      ) {
        // ve、可画编辑器新开页
        openUrl(targetPathUrl)
        return
      }
      // 非编辑器等新开页，使用next router跳转
      targetPathUrl && router.push(targetPathUrl)
      setConfirmLoading(false)
      return
    }
    if (res?.code === 201011063) {
      // 新设备，切换验证码登录，并展示风险提示文案
      changeLoginTab(LOGIN_TAB_TYPE.CODE, true)
    }
    setConfirmLoading(false)
  }

  const loginFetch = async (paramsData: any, type) => {
    await globalMobx.login(paramsData, type, (res, targetUrl) =>
      handleCallback(res, targetUrl, type),
    )
  }

  const handleSubmit = async (values, type: string) => {
    if (confirmLoading) return
    setConfirmLoading(true)
    let origin: any =
      Cookies.get(COOKIES_MAP.GIVEAWAY_CHANNEL, { domain: '.creativebooster.com' }) || ''
    if (origin?.toLocaleUpperCase() === 'SSO') {
      origin = ''
    }
    const assignData: any = {}
    if (isFbe) {
      assignData.origin = 'FBE'
    } else {
      if (origin) {
        assignData.origin = origin
      }
    }
    const paramsData: any = Object.assign(assignData, values)
    paramsData.account = trim(paramsData.account)
    checkValidateFetch().then(async (isValidate: boolean) => {
      if (!isValidate) {
        await loginFetch(paramsData, type)
        return
      }
      try {
        const { validate } = await captchaInsRef.current.verify()
        paramsData.validate = validate
        if (validate) {
          await loginFetch(paramsData, type)
          if (captchaInsRef.current?.open) {
            setConfirmLoading(false)
          }
        }
      } catch (error) {
        if ('open' in error && !error.open) {
          setConfirmLoading(false)
        }
        console.error('登录提交失败: ', error)
      }
    })
  }

  /**
   * 登录提交
   */
  const handleOk = async (form, type: string) => {
    form.validateFields().then(async ({ protocol, ...values }) => {
      if (!protocol) {
        modal.confirm({
          title: t('user:t42'),
          centered: true,
          content: (
            <div style={{ fontSize: 12 }}>
              <Trans i18nKey="t40" ns="user">
                我已阅读并同意
                <span
                  style={{ fontSize: 12 }}
                  className="link-button"
                  onClick={() => {
                    window.open('/agreement')
                  }}
                >
                  用户协议
                </span>
                和
                <span
                  style={{ fontSize: 12 }}
                  className="link-button"
                  onClick={() => {
                    window.open('/policy')
                  }}
                >
                  隐私政策
                </span>
              </Trans>
            </div>
          ),
          onOk: async () => {
            form.setFieldsValue({ protocol: true })
            await handleSubmit(values, type)
          },
          okText: t('user:t43'),
          cancelText: t('components:t85'),
        })
        return
      }
      await handleSubmit(values, type)
    })
  }

  /**
   * 发送验证码
   * @param startFn
   */
  const handleSendCode = async (startFn: () => void) => {
    try {
      const values = await mobileForm.validateFields(['account'])
      const account = values.account
      if (loginMobileReg.test(account) || emailReg.test(account)) {
        // 人机校验
        const { validate } = await captchaInsRef.current.verify()
        startFn()
        const { data = {} } = await sendVerifyCode({ account, validate })
        if (data && data.code === 0) {
          message.success(t('user:t1'))
          process.env.NODE_ENV !== 'production' &&
            data.result &&
            mobileForm.setFieldsValue({ captcha: data.result })
        }
      } else {
        mobileForm.setFields([
          {
            name: ['account'],
            errors: [t('user:t14')],
            touched: false,
          },
        ])
      }
    } catch (error) {
      console.error('发送验证码: ', error)
    }
  }

  // 手机验证码登录
  const MobileLogin = () => {
    return (
      <div>
        <Form layout="vertical" requiredMark={false} className={styles.main} form={mobileForm}>
          {globalMobx.isNewDevice && (
            <div style={{ color: '#ff0000', marginBottom: 20, fontSize: 12 }}>{t('user:t41')}</div>
          )}
          <Form.Item
            style={{ marginBottom: 30 }}
            label=""
            name="account"
            rules={[
              { required: true, message: t('user:t13'), whitespace: true },
              () => ({
                validator(_, value) {
                  const v = (value || '').trim()
                  if (v && !loginMobileReg.test(v) && !emailReg.test(v)) {
                    return Promise.reject(new Error(t('user:t14')))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input
              suffix=" "
              size="large"
              placeholder={t('user:t15')}
              className="cover-autofill-bg-color"
            />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Form.Item
              className={styles.password}
              style={{
                flex: 1,
                display: 'inline-block',
                marginBottom: 30,
                marginRight: 16,
              }}
              label=""
              name="captcha"
              rules={[
                { required: true, message: t('user:t2'), whitespace: true },
                () => ({
                  validator(_, value) {
                    const v = (value || '').trim()
                    if (v && !verifyCodeReg.test(v)) {
                      return Promise.reject(new Error(t('user:t3')))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                style={{ width: '100%' }}
                suffix=" "
                size="large"
                placeholder={t('user:t4')}
                className="cover-autofill-bg-color"
              />
            </Form.Item>
            <Form.Item
              style={{
                width: '130px',
                textAlign: 'right',
                display: 'inline-block',
                marginBottom: 30,
              }}
            >
              {' '}
              <div className="psd-label">
                <CountDown
                  seconds={60}
                  render={(onStart, disabled, current) => {
                    if (disabled) {
                      return (
                        <Button
                          className={classNames(styles['verify-code-disabled'], styles['get-code'])}
                          size="large"
                          disabled={disabled}
                        >{`${current}s ${t('user:t5')}`}</Button>
                      )
                    }
                    return (
                      <Button
                        onClick={() => handleSendCode(onStart)}
                        size="large"
                        className={styles['get-code']}
                      >
                        {t('user:t6')}
                      </Button>
                    )
                  }}
                />
              </div>
            </Form.Item>
          </div>
          <Form.Item
            style={{ marginBottom: 28 }}
            name="protocol"
            valuePropName="checked"
            // rules={[
            //   {
            //     validator: (_, value) =>
            //       value ? Promise.resolve() : Promise.reject(new Error(t('user:t32'))),
            //   },
            // ]}
          >
            <Checkbox className={styles['read-protocol-wrap']}>
              {/* id 探活使用，不可删除 */}
              <div style={{ fontSize: 12 }}>
                <Trans i18nKey="t40" ns="user">
                  我已阅读并同意
                  <span
                    style={{ fontSize: 12 }}
                    className="link-button"
                    onClick={e => {
                      e.stopPropagation()
                      openUrl('/agreement')
                    }}
                  >
                    用户协议
                  </span>
                  和
                  <span
                    style={{ fontSize: 12 }}
                    className="link-button"
                    onClick={e => {
                      e.stopPropagation()
                      openUrl('/policy')
                    }}
                  >
                    隐私政策
                  </span>
                </Trans>
              </div>
            </Checkbox>
          </Form.Item>
          <div className={styles['submit-form-item']}>
            <Button
              block
              type="primary"
              className={styles['submit-button']}
              loading={confirmLoading}
              onClick={() => handleOk(mobileForm, 'mobile')}
            >
              {`${t('user:t7')}/${t('user:t8')}`}
            </Button>
          </div>

          {!isFbe && (
            <div className={styles['sso-wrapper']}>
              <Divider plain className={styles['divider-line']}>
                {t('user:t10')}
              </Divider>
              <Button
                className={styles['sso-button']}
                onClick={() => {
                  // goSSO(`${window.location.origin}${globalMobx.targetPathUrl}`)
                  goSSO(`${window.location.origin}/sso`)
                }}
              >
                {t('user:t11')}
              </Button>
            </div>
          )}
        </Form>
      </div>
    )
  }

  // 账号密码登录
  const AccountLogin = () => {
    return (
      <div>
        <div className={styles.register}>
          <span className={styles['new-user']}>{t('user:t27')}？</span>
          <span
            className={classNames('link-button', styles['register-button'])}
            onClick={() => {
              globalMobx.currentAction = 'register'
            }}
          >
            {t('user:t12')}
          </span>
        </div>
        <div className={styles['lrf-main']}>
          <Form
            layout="vertical"
            requiredMark={false}
            name="normal_login"
            className={styles.main}
            form={accountForm}
          >
            <Form.Item
              name="account"
              style={{ marginBottom: 30 }}
              rules={[
                {
                  required: true,
                  message: t('user:t13'),
                  whitespace: true,
                },
                () => ({
                  validator(_, value) {
                    const v = (value || '').trim()
                    if (v && !loginMobileReg.test(v) && !emailReg.test(v)) {
                      return Promise.reject(new Error(t('user:t14')))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                suffix=" "
                placeholder={t('user:t15')}
                className={classNames('cover-autofill-bg-color', styles['cmp-input'])}
                size="large"
              />
            </Form.Item>
            <Form.Item
              className={styles.password}
              style={{ marginBottom: 30 }}
              name="password"
              rules={[
                {
                  required: true,
                  message: t('user:t16'),
                  whitespace: true,
                },
              ]}
            >
              <Input.Password
                placeholder={t('user:t18')}
                className={classNames('cover-autofill-bg-color', styles['cmp-input'])}
                type="password"
                size="large"
                maxLength={16}
              />
            </Form.Item>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: 28,
              }}
            >
              <Form.Item
                name="protocol"
                valuePropName="checked"
                noStyle
                // rules={[
                //   {
                //     validator: (_, value) =>
                //       value ? Promise.resolve() : Promise.reject(new Error(t('user:t32'))),
                //   },
                // ]}
              >
                <Checkbox className={styles['read-protocol-wrap']}>
                  {/* id 探活使用，不可删除 */}
                  <div style={{ fontSize: 12 }} id='d8e362'>
                    <Trans i18nKey="t40" ns="user">
                      我已阅读并同意
                      <span
                        style={{ fontSize: 12 }}
                        className="link-button"
                        onClick={e => {
                          e.stopPropagation()
                          openUrl('/agreement')
                        }}
                      >
                        用户协议
                      </span>
                      和
                      <span
                        style={{ fontSize: 12 }}
                        className="link-button"
                        onClick={e => {
                          e.stopPropagation()
                          openUrl('/policy')
                        }}
                      >
                        隐私政策
                      </span>
                    </Trans>
                  </div>
                </Checkbox>
              </Form.Item>
              <div className={styles['forgot-password']} style={{ marginTop: 0 }}>
                <span
                  className={classNames('link-button', styles['register-button'])}
                  onClick={() => {
                    globalMobx.currentAction = 'forgot'
                  }}
                >
                  {t('user:t19')}?
                </span>
              </div>
            </div>
            <div className={styles['submit-form-item']}>
              <Button
                id="d8e361" // 探活使用，不可删除
                type="primary"
                className={styles['submit-button']}
                loading={confirmLoading}
                onClick={() => handleOk(accountForm, 'account')}
              >
                {t('user:t20')}
              </Button>
            </div>
            {!isFbe && (
              <div className={styles['sso-wrapper']}>
                <Divider plain className={styles['divider-line']}>
                  {t('user:t10')}
                </Divider>
                <Button
                  className={styles['sso-button']}
                  onClick={() => {
                    // goSSO(`${window.location.origin}${globalMobx.targetPathUrl}`)
                    goSSO(`${window.location.origin}/sso`)
                  }}
                >
                  {t('user:t11')}
                </Button>
              </div>
            )}
          </Form>
        </div>
      </div>
    )
  }

  return (
    <BaseLayout>
      <Tabs
        style={{ marginTop: 44 }}
        className={styles['login-tabs']}
        centered
        activeKey={globalMobx.lastLoginTab}
        onChange={(val: string) => changeLoginTab(val, false)}
      >
        <Tabs.TabPane tab={t('user:t29')} key={LOGIN_TAB_TYPE.CODE}>
          <MobileLogin />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('user:t30')} key={LOGIN_TAB_TYPE.ACCOUNT}>
          <AccountLogin />
        </Tabs.TabPane>
      </Tabs>
      <Captcha ref={captchaInsRef} lang={language} />
      {contextHolder}
    </BaseLayout>
  )
}
export default observer(Login)
